import {
  TableCellProps,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';
import useAsyncFn, { AsyncState } from 'react-use/lib/useAsyncFn';
import { rbacApiRef } from '../../..';
import { useOverviewContext } from './OverviewContext';
import { PermissionEntity } from './ManageRolePermissionsDrawer';

export interface Filters {
  text?: string;
}

export interface RolePermissionsTableContextProviderProps {
  children?: ReactNode;
}

export interface RolePermissionsTableContextType
  extends RolePermissionsTableContextProviderProps {
  openManageDrawer: boolean;
  setOpenManageDrawer: Dispatch<SetStateAction<boolean>>;

  error: Error | undefined;
  setError: Dispatch<SetStateAction<Error | undefined>>;

  openConfirmDialog: boolean;
  setOpenConfirmDialog: Dispatch<SetStateAction<boolean>>;

  removePermissions: (permissions: string[]) => Promise<string[]>;
  removePermissionsState: AsyncState<string[]>;

  loadPermissions: () => Promise<{
    permissions: PermissionEntity[];
    total: number;
  }>;
  permissionsLoaded: AsyncState<
    | {
        permissions: PermissionEntity[];
        total: number;
      }
    | undefined
  >;

  loadFilteredPermissions: () => Promise<{
    permissions: PermissionEntity[];
    total: number;
  }>;
  filteredPermissions: AsyncState<
    | {
        permissions: PermissionEntity[];
        total: number;
      }
    | undefined
  >;

  updateRole: (permissions: PermissionEntity[]) => Promise<void>;

  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;

  defaultColumns: TableCellProps<PermissionEntity>[];

  selectedDeleteItems: PermissionEntity[];
  setSelectedDeleteItems: Dispatch<SetStateAction<PermissionEntity[]>>;

  selectedPermissions: PermissionEntity[];
  setSelectedPermissions: Dispatch<SetStateAction<PermissionEntity[]>>;
}

export const RolePermissionsTableContext =
  createContext<RolePermissionsTableContextType>(
    {} as RolePermissionsTableContextType,
  );

export const RolePermissionsTableContextProvider = ({
  children,
}: RolePermissionsTableContextProviderProps) => {
  const { roleId } = useOverviewContext();
  const rbacApi = useApi(rbacApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);

  const [error, setError] = useState<Error>();

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openManageDrawer, setOpenManageDrawer] = useState(false);

  const [filters, setFilters] = useState<Filters>({});

  const [selectedDeleteItems, setSelectedDeleteItems] = useState<
    PermissionEntity[]
  >([]);

  const [selectedPermissions, setSelectedPermissions] = useState<
    PermissionEntity[]
  >([]);

  const defaultColumns: TableCellProps<PermissionEntity>[] = [
    {
      field: 'displayName',
      headerName: 'Name',
      sortable: true,
    },
    {
      field: 'description',
      tooltip: true,
      headerName: 'Description',
    },
  ];

  const [permissionsLoaded, loadPermissions] = useAsyncFn(async () => {
    const fetchPermissions = async (): Promise<{
      permissions: PermissionEntity[];
      total: number;
    }> => {
      const res = await rbacApi.getRolePermissions({
        roleId,
        options: {
          token: (await identityApi.getCredentials()).token,
        },
      });

      return {
        total: res.total,
        permissions: res.permissions.map(p => ({
          ...p,
          displayName: p.displayName ?? p.id,
        })),
      };
    };

    return fetchPermissions();
  }, []);

  const [filteredPermissions, loadFilteredPermissions] =
    useAsyncFn(async () => {
      const fetchPermissions = async (): Promise<{
        permissions: PermissionEntity[];
        total: number;
      }> => {
        const res = await rbacApi.getRolePermissions({
          searchKeyword: filters.text,
          roleId,
          options: {
            token: (await identityApi.getCredentials()).token,
          },
        });

        return {
          total: res.total,
          permissions: res.permissions.map(p => ({
            ...p,
            displayName: p.displayName ?? p.id,
          })),
        };
      };

      return fetchPermissions();
    }, [filters]);

  const updateRole = async (permissions: PermissionEntity[]) => {
    try {
      const token = (await identityApi.getCredentials()).token;
      const permissionIds = permissions.map(p => p.id);

      const res = await rbacApi.updateRolePermissions({
        roleId,
        permissions: permissionIds,
        options: { token },
      });

      setOpenManageDrawer(false);

      let message = 'Permissions updated successfully. ';
      if (
        res.addedPermissions.length > 0 ||
        res.removedPermissions.length > 0
      ) {
        message += 'Changes: ';
        if (res.addedPermissions.length > 0) {
          message += `Added: ${res.addedPermissions.length}. `;
        }
        if (res.removedPermissions.length > 0) {
          message += `Removed: ${res.removedPermissions.length}. `;
        }
      }

      alertApi.post({ message, severity: 'success' });

      if (!!res.errors?.length) {
        alertApi.post({ message: res.errors?.join(', '), severity: 'error' });
      }

      loadPermissions();
      loadFilteredPermissions();
    } catch (e) {
      alertApi.post({
        message: `${e.body.error.message}: ${e.body.error.errorMessage}`,
        severity: 'error',
      });
    }
  };

  const [removePermissionsState, removePermissions] = useAsyncFn(
    async (permissions: string[]) => {
      try {
        const token = (await identityApi.getCredentials()).token;

        const results = await Promise.allSettled(
          permissions.map(permissionId =>
            rbacApi.deleteRolePermissions({
              filters: { roleId, permissionId },
              options: { token },
            }),
          ),
        );

        const failed = results.filter(result => result.status === 'rejected');

        if (failed.length > 0) {
          failed.forEach(failure => {
            const customError = (failure as any).reason.body.error;
            alertApi.post({
              message: `${customError.message}: ${customError.errorMessage}`,
              severity: 'error',
            });
          });
        } else {
          alertApi.post({
            message: `Permissions successfully removed`,
            severity: 'success',
          });
        }

        loadPermissions();
        loadFilteredPermissions();
      } catch (e) {
        alertApi.post({
          message: `${e.message}: ${e.errorMessage}` || 'An error occurred',
          severity: 'error',
        });
      }
      return [];
    },
    [loadFilteredPermissions],
  );

  return (
    <RolePermissionsTableContext.Provider
      value={{
        openManageDrawer,
        setOpenManageDrawer,

        defaultColumns,

        loadPermissions,
        permissionsLoaded,

        filteredPermissions,
        loadFilteredPermissions,

        error,
        setError,

        openConfirmDialog,
        setOpenConfirmDialog,

        removePermissions,
        removePermissionsState,

        filters,
        setFilters,

        selectedDeleteItems,
        setSelectedDeleteItems,

        selectedPermissions,
        setSelectedPermissions,

        updateRole,
      }}
    >
      {children}
    </RolePermissionsTableContext.Provider>
  );
};

export const useRolePermissionsTableContext = () => {
  const context = useContext(RolePermissionsTableContext);
  if (!context) {
    throw new Error(
      'useRolePermissionsTableContext must be used within a RolePermissionsTableContextProvider',
    );
  }
  return context as RolePermissionsTableContextType;
};
